import { sectionOpeningLetter } from "./section-opening.js";
import { fetchReadingUnit, sectionFocus, readingCoordinate, resolveReadingCoordinate } from "./reading-unit.js";
import { sourceNavigationCandidates, isLocatedSource, readerOrigin, sourceSheetUrl, isInternalSourceId, verifiedExternalSource, sourceTargetRef } from "./source-navigation.js";
// live.jsx — the data bridge. Connects the verbatim Mayanos design to the live
// MongoDB (via the Next BFF), reshaping real text + footnotes into the SICHA /
// SOURCES shapes the design already consumes. Curated-only layers are flagged.

(function () {
  window.MAYANOS_READING_UNITS = { readingCoordinate, resolveReadingCoordinate };
  const slugify = (ref) => encodeURIComponent(String(ref).replace(/ /g, "_"));
  const canonicalBookAliases = { "ayin-beis": "Hemshech Ayin Beis Vol 1" };
  const canonicalBookSlug = (book) => {
    const raw = String(book || "").replace(/_/g, " ").trim();
    if (/ayin\s*beis|ayin\s*bais|עין\s*בית|תערב/i.test(raw)) return "ayin-beis";
    const melukatVolume = raw.match(/\bvol(?:ume)?\.?\s*0*(\d+)/i);
    if (/maamarim.*melukat|melukat.*maamarim|melukatim/i.test(raw)) return `maamarim-melukat-${melukatVolume?.[1] || "1"}`;
    return raw.toLowerCase()
      .replace(/\b(hemshech|vol(?:ume)?|part)\b/g, " ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "reader";
  };
  const canonicalNumbersFromRef = (ref) => {
    const raw = String(ref || "");
    return {
      // The clean route calls this slot “maamar”, but the same position is
      // called “sicha” in Likkutei Sichos and related collections.
      maamar: raw.match(/(?:^|[: ,])(?:maamar|sicha)[: _-]*(\d+)/i)?.[1] || "",
      seif: raw.match(/(?:^|[: ,])(?:seif|section)[: _-]*(\d+)/i)?.[1] || "",
    };
  };
  const canonicalNumbersFromSection = (section) => {
    if (!section) return {};
    const path = Array.isArray(section.path) ? section.path.join(" ") : "";
    const label = String(section.label || section.heLabel || "");
    const reference = String(section.readerRef || section.targetRef || section.ref || "");
    return {
      maamar: path.match(/(?:maamar|sicha)\s*(\d+)/i)?.[1] || label.match(/(?:maamar|sicha)\s*(\d+)/i)?.[1] || reference.match(/(?:maamar|sicha)\s*[: ,_-]*(\d+)/i)?.[1] || "",
      seif: label.match(/(?:section|seif|סעיף)\s*([0-9]+)/i)?.[1] || reference.match(/(?:section|seif)\s*[: ,_-]*(\d+)/i)?.[1] || label.match(/(\d+)(?!.*\d)/)?.[1] || "",
    };
  };
  const canonicalSectionForRef = (toc, ref) => {
    const sections = toc && Array.isArray(toc.sections) ? toc.sections.filter((section) => section && !section.isHeader) : [];
    const value = String(ref || "");
    return sections.find((section) => section.ref === value || section.readerRef === value || section.targetRef === value)
      || sections.filter((section) => {
        const candidate = section.ref || section.readerRef || section.targetRef || "";
        return value.indexOf(candidate + ":") === 0;
      }).sort((a, b) => String(b.ref || b.readerRef || b.targetRef || "").length - String(a.ref || a.readerRef || a.targetRef || "").length)[0]
      || null;
  };
  // Repeating display refs push the bare corpus id into `readerRef`, so the
  // owning volume must be read from `ref` first. Otherwise the slug is the id
  // and every such section looks like position 1 of its own book.
  const canonicalSectionBook = (section) => {
    const candidates = [section && section.ref, section && section.readerRef, section && section.targetRef];
    for (const value of candidates) {
      const named = String(value || "").match(/^(.*?\bVol(?:ume)?\.?\s*\d+)(?:,|$)/i);
      if (named) return named[1];
    }
    // Books without a volume number keep their name in the first comma part of
    // `ref`; `readerRef` may be a bare corpus id when display refs repeat.
    return String((section && (section.ref || section.readerRef || section.targetRef)) || "").split(",")[0];
  };
  const canonicalReaderPathFor = (book, ref, segmentRef, toc) => {
    const coordinate = readingCoordinate(toc || window.__mayanosTOC, ref || segmentRef || "");
    if (coordinate) {
      const slug = canonicalBookSlug(coordinate.book);
      // Same rule as below: never publish an address nobody can resolve.
      if (!slug || slug === "reader") return "";
      return `/r/${slug}/${coordinate.maamar}/${coordinate.seif}`;
    }
    const section = canonicalSectionForRef(toc || window.__mayanosTOC, ref || segmentRef);
    const fromRef = canonicalNumbersFromRef(section && (section.readerRef || section.targetRef || section.ref) || ref || segmentRef);
    const fromSection = canonicalNumbersFromSection(section);
    const maamar = fromRef.maamar || fromSection.maamar;
    const seif = fromRef.seif || fromSection.seif;
    if (!maamar || !seif) return "";
    // A Melukat TOC is intentionally a six-volume index, so toc.book is the
    // combined label rather than the volume that owns this section. Derive
    // the route book from the section ref first; otherwise Vol 1 becomes the
    // default for every identical maamar number across the six volumes.
    const sectionBook = canonicalSectionBook(section);
    const slug = canonicalBookSlug(sectionBook || book || window.SICHA && (window.SICHA.collection || window.SICHA.titleEn));
    // Books that cannot produce a slug (their name is Hebrew only) keep the
    // working /reader/<ref> address instead of an address nobody can resolve.
    if (!slug || slug === "reader") return "";
    return `/r/${slug}/${maamar}/${seif}`;
  };
  const canonicalRouteSection = (toc, maamar, seif, book) => {
    const coordinate = resolveReadingCoordinate(toc, book, maamar, seif, canonicalBookSlug);
    if (coordinate) return coordinate;
    const sections = toc && Array.isArray(toc.sections) ? toc.sections.filter((section) => section && !section.isHeader) : [];
    const matches = sections.filter((section) => {
      const numbers = canonicalNumbersFromSection(section);
      return numbers.maamar === String(maamar) && numbers.seif === String(seif);
    });
    if (matches.length <= 1) return matches[0] || null;
    const wantedBook = canonicalBookSlug(book);
    const owned = matches.filter((section) => {
      const sectionBook = canonicalSectionBook(section);
      return sectionBook && canonicalBookSlug(sectionBook) === wantedBook;
    });
    // Never silently open a same-number section from another volume. A
    // unique match remains safe for older TOCs that do not carry a volume in
    // their section refs.
    return owned.length === 1 ? owned[0] : null;
  };
  const MARK_L = "‹", MARK_R = "›"; // ‹ ›  (the reader's footnote-marker delimiters)

  window.MAYANOS_STATUS = { state: "loading", message: "Connecting to the Mayanos library…" };

  // A reader route that resolves to nothing is a dead end, not a slow load:
  // without a terminal state the surface waits on "Connecting…" forever.
  const NOT_FOUND_MESSAGE = "We couldn't open that passage. It may have moved, or the link may be out of date.";

  // The reader can name what it is opening, so the waiting screen says which
  // text is coming rather than a generic line.
  const describeTarget = (refOrTitle) => {
    const value = String(refOrTitle || "").replace(/\s+/g, " ").trim();
    if (!value) return "";
    // A bare corpus id is not a name a reader would recognize.
    if (/^\d+:/.test(value)) return "";
    // The first comma part is the book ("Toras Menachem Vol 41", "Hayom Yom");
    // later parts are the section inside it, which is not what is opening.
    const parts = value.split(",").map((part) => part.trim()).filter(Boolean);
    const name = parts[0] || value;
    return name.length > 48 ? `${name.slice(0, 45).trim()}…` : name;
  };

  function setStatus(state, message, target) {
    window.MAYANOS_STATUS = { state, message: message || "", target: target || "" };
    try { window.dispatchEvent(new CustomEvent("mayanos-status", { detail: window.MAYANOS_STATUS })); } catch (e) {}
    if (window.__mayanosRerender) window.__mayanosRerender();
    if (state === "unavailable" && window.MAYANOS_ANALYTICS) {
      window.MAYANOS_ANALYTICS.track("error", { surface: "library", message: message || "Library unavailable" });
    }
  }

  async function getJSON(url, options) {
    for (let attempt = 0; attempt < 2; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const r = await fetch(url, Object.assign({ headers: { Accept: "application/json" } }, options || {}, { signal: options?.signal ? AbortSignal.any([controller.signal, options.signal]) : controller.signal }));
        if (r.ok) return await r.json();
        // Remember a missing ref so the reader can say "not found" rather than
        // reporting the whole library as unavailable.
        if (r.status === 404) { try { window.__mayanosMissingRef = String(url); } catch (markError) {} }
        if (r.status === 429 && attempt === 0) {
          await new Promise(resolve => setTimeout(resolve, Math.min(5, Number(r.headers.get("Retry-After")) || 2) * 1000));
          continue;
        }
        if (r.status >= 400 && r.status < 500) return null;
      } catch (e) {
        if (e && e.name === "AbortError") return null;
        if (attempt === 1) return null;
      } finally {
        clearTimeout(timer);
      }
      await new Promise((resolve) => setTimeout(resolve, 240 * (attempt + 1)));
    }
    return null;
  }

  async function resolveCanonicalReaderRoute(slug, maamar, seif) {
    const wanted = String(slug || "").toLowerCase();
    let book = canonicalBookAliases[wanted] || "";
    if (!book && window.SICHA) {
      const currentBook = window.SICHA.collection || window.SICHA.titleEn || "";
      if (canonicalBookSlug(currentBook) === wanted) book = currentBook;
    }
    if (!book) {
      const library = await getJSON(`/bff/library?slug=${encodeURIComponent(wanted)}`);
      const collections = library && Array.isArray(library.collections) ? library.collections : [];
      const match = collections.find((item) => canonicalBookSlug(item.en || item.ref || item.id) === wanted);
      book = match && (match.en || match.ref || match.id) || "";
    }
    if (!book) return null;
    const toc = await getJSON(`/bff/toc/${slugify(book)}?sections=1`);
    const section = canonicalRouteSection(toc, maamar, seif, book);
    if (!section) return null;
    return {
      book,
      toc,
      ref: section.readerRef || section.targetRef || section.ref,
      path: `/r/${wanted}/${maamar}/${seif}`,
    };
  }

  // Quote discovery is derived from live text/search data. Never let a
  // previously cached empty response hide a later successful match. The
  // versioned URL also invalidates older browser/CDN entries after rollout.
  function getQuoteLinks(ref) {
    return getJSON(`/bff/quote-links/${slugify(ref)}?v=5`);
  }

  // Beta discovery is deliberately kept outside SOURCES. It may suggest a
  // plausible Chassidus target, but it is not a confirmed citation and must
  // never replace or alter the existing footnote/link graph.
  const betaFootnoteCache = new Map();
  function notifyBetaFootnotes() {
    try { window.dispatchEvent(new CustomEvent("mayanos-beta-footnotes")); } catch (e) {}
    if (window.__mayanosRerender) window.__mayanosRerender();
  }
  async function loadBetaFootnotes(source) {
    if (!source || !source.footnote) return [];
    const text = stripMarkers([source.title, source.he, source.en].filter(Boolean).join(" ")).slice(0, 2400);
    if (!text.trim()) return [];
    const key = `${source.anchorRef || source.id || "source"}|${source.n || ""}|${text}`;
    const cached = betaFootnoteCache.get(key);
    if (cached) {
      window.BETA_FOOTNOTES = (window.BETA_FOOTNOTES || []).filter((item) => item.key !== key).concat(cached);
      notifyBetaFootnotes();
      return cached;
    }
    const loading = [{
      key, id: `beta-footnote-${source.n}`, sourceN: source.n, marker: source.n,
      anchorRef: source.anchorRef || "", title: source.title || `Footnote ${source.n}`,
      text, state: "loading", matches: [],
    }];
    window.BETA_FOOTNOTES = (window.BETA_FOOTNOTES || []).filter((item) => item.key !== key).concat(loading);
    notifyBetaFootnotes();
    const query = `/bff/beta-footnotes?${new URLSearchParams({
      sourceRef: source.anchorRef || source.srcRef || "",
      marker: String(source.n || ""),
      text,
    }).toString()}`;
    const response = await getJSON(query);
    const ready = [{
      key, id: `beta-footnote-${source.n}`, sourceN: source.n, marker: source.n,
      anchorRef: source.anchorRef || "", title: source.title || `Footnote ${source.n}`,
      text, state: response ? "ready" : "unavailable", matches: response && Array.isArray(response.matches) ? response.matches : [],
      notice: response && response.notice || "Beta suggestions only; existing links are unchanged.",
    }];
    betaFootnoteCache.set(key, ready);
    if (window.SICHA && window.SOURCES && window.SOURCES.some((item) => item.id === source.id)) {
      window.BETA_FOOTNOTES = (window.BETA_FOOTNOTES || []).filter((item) => item.key !== key).concat(ready);
      notifyBetaFootnotes();
    }
    return ready;
  }

  // The corpus contains both real HTML and a small number of HTML-encoded
  // values. Decode/strip repeatedly so neither form leaks markup into the
  // reader, source rail, or related-resource descriptions.
  const stripTags = (h) => {
    let value = String(h || "");
    for (let pass = 0; pass < 3; pass++) {
      const d = document.createElement("div");
      d.innerHTML = value;
      const next = d.textContent || "";
      if (next === value) break;
      value = next;
    }
    return value
      .replace(/<\s*\/?\s*[a-z][^>]*>/gi, " ")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };
  const stripMarkers = (s) => String(s || "")
    .replace(/‹\d+›/g, "")
    .replace(/(?:\*\*|__)(.*?)(?:\*\*|__)/g, "$1")
    .replace(/(?:\*|_)(.*?)(?:\*|_)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
  const isInternalFootnoteId = isInternalSourceId;
  const footnoteLabel = (title, body) => {
    const rawTitle = stripTags(title || "").replace(/[:\s]+$/, "");
    if (rawTitle && !isInternalFootnoteId(rawTitle)) return rawTitle;
    const text = stripTags(body || "");
    return text.length > 110 ? `${text.slice(0, 107).trim()}…` : (text || "Footnote");
  };
  const firstText = (items, key, max = 4) => (items || []).slice(0, max).map((s) => stripTags(s && s[key])).filter(Boolean).join(" ");
  const normalizeForMatch = (s) => stripTags(stripMarkers(s))
    .replace(/[\u0591-\u05C7]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\u0590-\u05ff]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const stopWords = new Set("the and that this with from before after into your you are him her his its not for but cf ch end chapter source taught been have has were would should could there their them they who what when where which".split(" "));

  const classify = (c) => {
    c = String(c || "");
    if (/זהר|זוהר|עץ חיים|פרדס|קבלה|Zohar|Eitz Chaim|Etz Chaim|Pardes|Kabbalah/i.test(c)) return "Kabbalah";
    if (/בראשית|שמות|ויקרא|במדבר|דברים|תהלים|ישעי|ירמי|יחזקאל|משלי|איוב|דניאל|Genesis|Exodus|Leviticus|Numbers|Deuteronomy|Psalms|Isaiah|Proverbs|Bereishis|Bereshit|Bereishit|Shemos|Shemot|Vayikra|Bamidbar|Devarim|Noach|Lech|Vayeira|Chayei|Toldot|Toldos|Vayetzei|Vayeitzei|Vayishlach|Vayeshev|Miketz|Mikeitz|Vayigash|Vayechi|Bo|Beshalach|Yisro|Yitro|Mishpatim|Terumah|Tetzaveh|Ki Sisa|Ki Tisa|Vayakhel|Pekudei|Tzav|Shemini|Tazria|Metzora|Acharei|Kedoshim|Emor|Behar|Bechukotai|Bechukosai|Naso|Behaalotecha|Behaaloscha|Shelach|Korach|Chukat|Chukas|Balak|Pinchas|Matot|Matos|Masei|Vaetchanan|Eikev|Reeh|Shoftim|Teitzei|Seitzei|Tavo|Nitzavim|Vayelech|Haazinu|Vezot|Vezos/i.test(c)) return "Tanach";
    if (/ברכות|שבת|עירובין|פסחים|יומא|סוכה|ביצה|תענית|מגילה|חגיגה|יבמות|כתובות|נדרים|גיטין|קידושין|בבא|סנהדרין|מכות|שבועות|מנחות|חולין|נדה|זבחים|Berachos|Berakhot|Shabbos|Eruvin|Pesachim|Yoma|Sukkah|Chagigah|Yevamos|Kesubos|Gittin|Kiddushin|Bava|Sanhedrin|Menachos|Chullin|Niddah/i.test(c)) return "Gemara";
    if (/משנה|אבות|Mishnah|Pirkei Avos|Avot/i.test(c)) return "Mishnah";
    if (/מדרש|רבה|תנחומא|ספרי|מכילתא|פסיקתא|ילקוט|Midrash|Rabbah|Tanchuma|Sifrei|Mechilta|Yalkut/i.test(c)) return "Midrash";
    if (/רמב.?ם|שולחן|שו.?ע|טור|הלכות|רי.?ף|רא.?ש|Rambam|Mishneh Torah|Shulchan|Tur|Hilchos|Halacha/i.test(c)) return "Halacha";
    if (/תניא|תורה אור|לקוטי תורה|מאמרי|קונטרס|Tanya|Torah Or|Likkutei Torah|Maamar/i.test(c)) return "Chassidus";
    if (/לקוטי שיחות|אגרות|רשימות|התוועדויות|Likkutei Sichos|Igros|Reshimos|Hayom Yom/i.test(c)) return "Rebbe";
    return "Other";
  };

  const displayCategory = (raw) => {
    raw = String(raw || "");
    if (/quoting commentary/i.test(raw)) return "Quoted Footnotes";
    if (/commentary/i.test(raw)) return "Commentary";
    if (raw === "Talmud") return "Talmud";
    if (raw === "Tanakh") return "Tanach";
    if (raw === "Halakhah") return "Halacha";
    if (raw === "Chasidut" || raw === "Chabad" || raw === "Chassidut") return "Chasidut";
    return raw || "Other";
  };

  const groupForText = (t, fallback) => {
    const cats = Array.isArray(t && t.categories) ? t.categories : [];
    if (cats.includes("Kabbalah") || /Zohar|זוהר|זהר/i.test(`${t && t.ref} ${t && t.title} ${fallback || ""}`)) return "Kabbalah";
    if (cats.includes("Mishnah") || /Pirkei Avot|Avot|משנה אבות/i.test(`${t && t.ref} ${t && t.title} ${fallback || ""}`)) return "Mishnah";
    if (cats.includes("Talmud") || cats.includes("Bavli") || cats.includes("Yerushalmi")) return "Talmud";
    if (cats.includes("Tanakh")) return "Tanach";
    if (cats.includes("Midrash")) return "Midrash";
    if (cats.includes("Halakhah") || cats.includes("Halacha")) return "Halacha";
    if (cats.includes("Chasidut") || cats.includes("Chabad") || cats.includes("Chassidut")) return "Chasidut";
    return displayCategory(fallback || cats[0] || classify(`${t && t.ref} ${t && t.title}`));
  };
  const typeForGroup = (group) => ({
    Talmud: "Gemara",
    Chasidut: "Chassidus",
    Tanach: "Tanach",
    Mishnah: "Mishnah",
    Midrash: "Midrash",
    Halacha: "Halacha",
    Kabbalah: "Kabbalah",
  }[group] || classify(group));

  // Pull footnotes out of one HTML string. Returns plain text with %%FN<idx>%%
  // tokens where markers were, plus the list of {title, body}.
  const extract = (html) => {
    const div = document.createElement("div");
    div.innerHTML = String(html || "");
    div.querySelectorAll("[data-overlay]").forEach((e) => e.remove()); // Vilna page markers
    const notes = [];
    // Most volumes use the canonical class, but a few Likkutei Sichos
    // segments contain an unclassed numeric <sup> immediately before the
    // footnote body. Treat both forms as one marker so the raw number never
    // becomes part of the reading text.
    const footnoteSupers = Array.from(div.querySelectorAll("sup")).filter((sup) => {
      const className = String(sup.className || "");
      const value = String(sup.textContent || "").trim();
      return /footnote/i.test(className) || (/^\d{1,3}$/.test(value) && sup.nextElementSibling && sup.nextElementSibling.matches && sup.nextElementSibling.matches("i.footnote"));
    });
    footnoteSupers.forEach((sup) => {
      const sib = sup.nextElementSibling;
      let title = "", body = "";
      if (sib && sib.matches && sib.matches("i.footnote")) {
        const b = sib.querySelector("b");
        if (b) { title = stripTags(b.innerHTML); b.remove(); }
        body = stripTags(sib.innerHTML);
        sib.remove();
      }
      sup.replaceWith(document.createTextNode("%%FN" + notes.length + "%%"));
      notes.push({ title, body });
    });
    // Preserve source emphasis before converting the remaining HTML to plain text.
    div.querySelectorAll("b, strong").forEach((node) => {
      node.replaceWith(document.createTextNode("**" + node.textContent + "**"));
    });
    return { text: stripTags(div.textContent || ""), notes };
  };

  // Build {segs, sources, lastN} from a /bff/text response, numbering footnotes
  // continuously from startN so appended sections keep incrementing.
  function buildSection(t, startN) {
    if (t.units) {
      let lastN = startN || 0;
      const parts = t.units.map((unit, index) => {
        const part = decorateSection(unit, buildSection(unit, lastN), index > 0);
        lastN = part.lastN;
        return part;
      });
      return { segs: parts.flatMap((part) => part.segs), sources: parts.flatMap((part) => part.sources), noteQuotes: parts.flatMap((part) => part.noteQuotes), lastN };
    }
    const sources = [];
    const noteQuotes = [];
    let gN = startN || 0;
    const segs = t.segments.map((s, index) => {
      const he = extract(s.he), en = extract(s.en);
      const count = Math.max(he.notes.length, en.notes.length);
      const mapN = [];
      for (let j = 0; j < count; j++) {
        gN++; mapN.push(gN);
        const hn = he.notes[j] || {}, ent = en.notes[j] || {};
        const rawTitle = (ent.title || hn.title || "").replace(/[:\s]+$/, "");
        const sourceRef = rawTitle && !isInternalFootnoteId(rawTitle) ? rawTitle : "";
        const anchorRef = s.ref ? `${s.ref}:${j + 1}` : "";
        const noteText = [hn.title, hn.body, ent.title, ent.body].filter(Boolean).join(" ");
        noteQuotes.push({
          n: gN,
          anchorRef,
          segmentRef: s.ref || "",
          text: noteText,
          quoteHe: [hn.title, hn.body].map(stripMarkers).filter(Boolean),
          quoteEn: [ent.title, ent.body].map(stripMarkers).filter(Boolean),
        });
        sources.push({
          n: gN, id: "fn-" + gN, type: classify(noteText),
          category: "Quoted Footnotes",
          displayCategory: "Quoted Footnotes",
          title: footnoteLabel(rawTitle, ent.body || hn.body), citation: "", relationship: "Footnote",
          he: hn.body || "", en: ent.body || "", ai: "", srcRef: sourceRef,
          anchorRef: s.ref || "", anchorRefs: s.ref ? [s.ref] : [],
          quoteKey: `${s.ref || s.id || "segment"}#${j + 1}`,
          quoted: true, footnote: true,
        });
      }
      const sub = (txt) => txt.replace(/%%FN(\d+)%%/g, (m, j) => (mapN[+j] != null ? MARK_L + mapN[+j] + MARK_R : ""));
      return { id: s.id, ref: s.ref, he: sub(he.text), en: sub(en.text), openingLetter: index === 0 ? sectionOpeningLetter(t, he.text) : "" };
    });
    return { segs, sources, lastN: gN, noteQuotes };
  }

  function hebrewSectionLetter(number) {
    const n = Number(number);
    const letters = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט", "י", "יא", "יב", "יג", "יד", "טו", "טז", "יז", "יח", "יט", "כ"];
    return letters[n] || String(n);
  }

  function sectionDividerLabel(t) {
    if (t && t.heRef && /[\u0590-\u05ff]/.test(t.heRef)) return t.heRef;
    const match = String(t && (t.ref || t.readerRef) || "").match(/(?:section|sif)[ _-]+(\d+)/i);
    return match ? "סעיף " + hebrewSectionLetter(match[1]) : String(t && (t.ref || t.readerRef) || "");
  }

  function decorateSection(t, built, withDivider) {
    if (t.units) { if (withDivider && built.segs[0]) built.segs[0]._section = sectionDividerLabel(t.units[0]); return built; }
    const navigationRef = t.tocTargetRef || t.ref;
    const readerRef = t.readerRef || t.ref;
    (built.segs || []).forEach((seg, index) => {
      seg._sectionRef = navigationRef;
      seg._sectionCanonicalRef = t.ref;
      seg._sectionReaderRef = readerRef;
      if (index === 0 && t.farbrengen && /:seif:1$/.test(navigationRef)) seg.farbrengen = t.farbrengen;
      if (withDivider && index === 0) seg._section = sectionDividerLabel(t);
    });
    return built;
  }

  function sectionRecord(t) {
    return {
      ref: t.readerRef || t.ref,
      canonicalRef: t.ref,
      targetRef: t.tocTargetRef || t.ref,
      heRef: t.heRef || t.ref,
      prev: t.prev || null,
      next: t.next || null,
    };
  }

  function historyObject(value) {
    return value && typeof value === "object" ? value : {};
  }

  function currentReaderOrigin(source) {
    const current = window.SICHA || {};
    const locationState = window.__mayanosCaptureReaderLocation?.() || window.__mayanosReaderLocation || {};
    const sectionRef = locationState.sectionRef || current._liveRef || current.id || "";
    const sourceAnchorRef = source && (source.anchorRef || (Array.isArray(source.anchorRefs) ? source.anchorRefs[0] : "")) || "";
    const segmentRef = sourceAnchorRef || locationState.segmentRef || current._focusRef || "";
    const segmentNumber = String(segmentRef || "").match(/:(\d+)(?:-\d+)?$/);
    const title = locationState.title || current.titleEn || sectionRef || "the text";
    return {
      url: location.pathname + location.search,
      ref: segmentRef || sectionRef,
      segmentRef,
      scrollTop: Math.max(0, Number(locationState.scrollTop) || 0),
      title,
      returnLabel: segmentNumber && segmentRef !== sectionRef ? `${title} · Segment ${segmentNumber[1]}` : title,
      sourceN: source && Number.isFinite(Number(source.n)) ? Number(source.n) : null,
      sourceRef: source && (source.srcRef || source.citation || "") || "",
      sourceAnchorRef,
    };
  }

  const BOOK_ALIASES = [
    ["Rosh Hashanah", "Rosh Hashanah"], ["Rosh Hashana", "Rosh Hashanah"], ["Bava Batra", "Bava Batra"], ["Bava Metzia", "Bava Metzia"], ["Bava Kamma", "Bava Kamma"],
    ["Pirkei Avot", "Pirkei Avot"], ["Pirkei Avos", "Pirkei Avot"], ["Avot", "Pirkei Avot"], ["Avos", "Pirkei Avot"],
    ["Berachos", "Berakhot"], ["Berachot", "Berakhot"], ["Berakhot", "Berakhot"], ["Brachos", "Berakhot"],
    ["Niddah", "Niddah"], ["Niddah", "Niddah"], ["Shabbat", "Shabbat"], ["Shabbos", "Shabbat"], ["Sanhedrin", "Sanhedrin"],
    ["Sukkah", "Sukkah"], ["Yoma", "Yoma"], ["Megillah", "Megillah"], ["Chagigah", "Chagigah"], ["Kiddushin", "Kiddushin"],
    ["Gittin", "Gittin"], ["Ketubot", "Ketubot"], ["Kesubos", "Ketubot"], ["Pesachim", "Pesachim"], ["Menachot", "Menachot"], ["Menachos", "Menachot"],
    ["Chullin", "Chullin"], ["Eruvin", "Eruvin"], ["Ta'anit", "Taanit"], ["Taanit", "Taanit"], ["Taanis", "Taanit"],
    ["Psalms", "Psalms"], ["Tehillim", "Psalms"], ["Tehilim", "Psalms"], ["Proverbs", "Proverbs"], ["Mishlei", "Proverbs"],
    ["Song of Songs", "Song of Songs"], ["Shir HaShirim", "Song of Songs"], ["Shir Hashirim", "Song of Songs"], ["Kohelet", "Ecclesiastes"], ["Koheles", "Ecclesiastes"],
    ["Genesis", "Genesis"], ["Bereishis", "Genesis"], ["Bereshit", "Genesis"], ["Bereishit", "Genesis"],
    ["Exodus", "Exodus"], ["Shemos", "Exodus"], ["Shemot", "Exodus"], ["Terumah", "Exodus"], ["Tetzaveh", "Exodus"], ["Ki Sisa", "Exodus"], ["Ki Tisa", "Exodus"],
    ["Leviticus", "Leviticus"], ["Vayikra", "Leviticus"], ["Vayikro", "Leviticus"], ["Tzav", "Leviticus"], ["Shemini", "Leviticus"], ["Acharei", "Leviticus"], ["Kedoshim", "Leviticus"], ["Emor", "Leviticus"], ["Behar", "Leviticus"], ["Bechukotai", "Leviticus"], ["Bechukosai", "Leviticus"],
    ["Numbers", "Numbers"], ["Bamidbar", "Numbers"], ["Naso", "Numbers"], ["Behaalotecha", "Numbers"], ["Behaaloscha", "Numbers"], ["Shelach", "Numbers"], ["Korach", "Numbers"], ["Chukat", "Numbers"], ["Chukas", "Numbers"], ["Balak", "Numbers"], ["Pinchas", "Numbers"], ["Matot", "Numbers"], ["Matos", "Numbers"], ["Masei", "Numbers"],
    ["Deuteronomy", "Deuteronomy"], ["Devarim", "Deuteronomy"], ["Vaetchanan", "Deuteronomy"], ["Eikev", "Deuteronomy"], ["Reeh", "Deuteronomy"], ["Shoftim", "Deuteronomy"], ["Ki Teitzei", "Deuteronomy"], ["Ki Seitzei", "Deuteronomy"], ["Ki Tavo", "Deuteronomy"], ["Nitzavim", "Deuteronomy"], ["Vayelech", "Deuteronomy"], ["Haazinu", "Deuteronomy"], ["Vezot Haberakhah", "Deuteronomy"], ["Vezos Haberacha", "Deuteronomy"],
    ["Isaiah", "Isaiah"], ["Yeshayahu", "Isaiah"], ["Yeshayah", "Isaiah"], ["Jeremiah", "Jeremiah"], ["Yirmiyahu", "Jeremiah"],
    ["Hosea", "Hosea"], ["Hoshea", "Hosea"], ["Hoshe’a", "Hosea"], ["Ezekiel", "Ezekiel"], ["Yechezkel", "Ezekiel"], ["Job", "Job"], ["Iyov", "Job"], ["Daniel", "Daniel"], ["Zohar", "Zohar"],
    ["Vayikra Rabbah", "Vayikra Rabbah"], ["Bereishit Rabbah", "Bereishit Rabbah"], ["Bereishis Rabbah", "Bereishit Rabbah"], ["Shemot Rabbah", "Shemot Rabbah"], ["Shemos Rabbah", "Shemot Rabbah"], ["Bamidbar Rabbah", "Bamidbar Rabbah"], ["Devarim Rabbah", "Devarim Rabbah"], ["Midrash Tehillim", "Midrash Tehillim"],
  ];
  const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const zoharSpecialRefs = (text) => {
    const raw = String(text || "");
    const refs = [];
    if (
      /Zohar\s+(?:III|3)\s*,?\s*(?:p\.?|page|daf)?\s*231/i.test(raw) ||
      /ז[וה]הר[^.׃:;\n]{0,24}(?:חלק\s*)?ג[׳'\"]?[^.׃:;\n]{0,20}(?:דף\s*)?רל[״"']?א/.test(raw)
    ) refs.push("Zohar, Pinchas 59:354");
    return refs;
  };
  const mishnahAvotSpecialRefs = (text) => {
    const raw = String(text || "");
    const refs = [];
    if (
      /(?:Mishnah\s+)?Avo[st][,\s]+(?:beginning\s+of\s+)?ch\.?\s*5/i.test(raw) ||
      /משנה\s+אבות\s+רפ[״"']?ה/.test(raw)
    ) refs.push("Pirkei Avot 5:1");
    return refs;
  };

  function citationRefsFromSource(src, segs) {
    const anchorRef = src && (src.anchorRef || (Array.isArray(src.anchorRefs) ? src.anchorRefs[0] : ""));
    const anchorSeg = (segs || []).find((seg) => anchorMatchesSegment(anchorRef, seg));
    const text = stripTags([
      anchorSeg && anchorSeg.he,
      anchorSeg && anchorSeg.en,
      src && src.title,
      src && src.srcRef,
      src && src.he,
      src && src.en,
    ].filter(Boolean).join(" "));
    const refs = [];
    zoharSpecialRefs(text).forEach((ref) => { if (!refs.includes(ref)) refs.push(ref); });
    mishnahAvotSpecialRefs(text).forEach((ref) => { if (!refs.includes(ref)) refs.push(ref); });
    for (const [alias, book] of BOOK_ALIASES) {
      const num = "\\d{1,3}[ab]?(?!\\d)";
      const re = new RegExp(`\\b${escapeRe(alias)}[,\\s]+(?:ch\\.?|chapter|p\\.?|page)?\\s*${num}(?::${num})?(?:[-–]${num})?`, "gi");
      let m;
      while ((m = re.exec(text))) {
        const suffix = m[0]
          .slice(m[0].toLowerCase().indexOf(alias.toLowerCase()) + alias.length)
          .replace(/^[,\s]+/, "")
          .replace(/^(?:ch\.?|chapter|p\.?|page)\s*/i, "")
          .trim();
        const ref = `${book} ${suffix}`.replace(/[.,;:)]+$/g, "");
        if (!refs.includes(ref)) refs.push(ref);
      }
    }
    return refs;
  }

  async function hydrateCitationSources(footnoteSources, startN, segs, onProgress) {
    const out = [];
    const seen = new Set();
    const tasks = [];
    let n = startN || 0;
    let lastProgress = 0;
    for (const src of footnoteSources || []) {
      const anchorRef = src.anchorRef || (Array.isArray(src.anchorRefs) ? src.anchorRefs[0] : "");
      for (const ref of citationRefsFromSource(src, segs)) {
        const key = `${anchorRef}|${ref}`;
        if (seen.has(key)) continue;
        seen.add(key);
        tasks.push({ src, anchorRef, ref });
      }
    }
    let cursor = 0;
    async function worker() {
      while (cursor < tasks.length) {
        const { src, anchorRef, ref } = tasks[cursor++];
        const t = await getJSON(`/bff/text/${slugify(ref)}`);
        if (!t || !Array.isArray(t.segments) || !t.segments.length) continue;
        n++;
        const group = groupForText(t, classify(`${t.ref} ${t.title} ${ref}`));
        const excerpt = bestCitationExcerpt(t, src, segs);
        out.push({
          n,
          id: `cite-${n}-${t.ref}`,
          type: typeForGroup(group),
          category: group,
          displayCategory: group,
          relationship: "Cited source",
          title: t.title || t.collection || ref.replace(/\s+\d.*$/, ""),
          citation: ref,
          he: excerpt.he || firstText(t.segments, "he"),
          en: excerpt.en || firstText(t.segments, "en"),
          ai: "",
          srcRef: ref,
          anchorRef,
          anchorRefs: anchorRef ? [anchorRef] : [],
          sourceHasEn: !!t.hasEnglish,
          derivedCitation: true,
          quoted: true,
          quoteKey: src.quoteKey || "",
          originFootnoteN: src.n,
          quoteHe: excerpt.quoteHe.concat([stripMarkers(src.he)]).filter(Boolean),
          quoteEn: excerpt.quoteEn.concat([stripMarkers(src.en)]).filter(Boolean),
        });
        if (onProgress && out.length > lastProgress) {
          lastProgress = out.length;
          onProgress(out.slice());
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(8, tasks.length) }, worker));
    return out;
  }

  // Whole-work Explore must not eagerly download the full text of every
  // citation in every footnote. Build openable records immediately and let
  // the node detail fetch its preview on demand. Existing citations remain
  // first-class map sources; this only changes when their text is fetched.
  function citationSourceStubs(footnoteSources, startN, segs) {
    const out = [];
    const seen = new Set();
    let n = startN || 0;
    for (const src of footnoteSources || []) {
      const anchorRef = src.anchorRef || (Array.isArray(src.anchorRefs) ? src.anchorRefs[0] : "");
      for (const ref of citationRefsFromSource(src, segs)) {
        const key = `${anchorRef}|${ref}`;
        if (seen.has(key)) continue;
        seen.add(key);
        n++;
        const group = displayCategory(classify(ref));
        const anchorSegment = (segs || []).find((seg) => anchorMatchesSegment(anchorRef, seg));
        out.push({
          n,
          id: `cite-stub-${n}-${ref}`,
          type: typeForGroup(group),
          category: group,
          displayCategory: group,
          relationship: "Cited source",
          title: ref.replace(/\s+\d.*$/, ""),
          citation: ref,
          he: "",
          en: "",
          srcRef: ref,
          anchorRef,
          anchorRefs: anchorRef ? [anchorRef] : [],
          sourceHasEn: true,
          derivedCitation: true,
          quoted: true,
          quoteKey: src.quoteKey || "",
          originFootnoteN: src.n,
          quoteHe: [stripMarkers(src.he)].filter(Boolean),
          quoteEn: [stripMarkers(src.en)].filter(Boolean),
          contextHe: anchorSegment ? stripMarkers(anchorSegment.he) : "",
          contextEn: anchorSegment ? stripMarkers(anchorSegment.en) : "",
        });
      }
    }
    return out;
  }

  function bestCitationExcerpt(t, src, segs) {
    const sourceSegments = Array.isArray(t && t.segments) ? t.segments : [];
    if (!sourceSegments.length) return { he: "", en: "", quoteHe: [], quoteEn: [] };
    const exact = sourceSegments.find(segment => segment.ref === t.focusRef || segment.ref === src?.srcRef);
    if (exact) return {
      he: stripTags(exact.he), en: stripTags(exact.en),
      quoteHe: [stripTags(exact.he)].filter(Boolean), quoteEn: [stripTags(exact.en)].filter(Boolean),
    };
    const anchorRef = src && (src.anchorRef || (Array.isArray(src.anchorRefs) ? src.anchorRefs[0] : ""));
    const anchorSeg = (segs || (window.SICHA && window.SICHA.segments) || []).find((seg) => anchorMatchesSegment(anchorRef, seg));
    const query = normalizeForMatch([
      anchorSeg && anchorSeg.he,
      anchorSeg && anchorSeg.en,
      src && src.title,
      src && src.srcRef,
      src && src.he,
      src && src.en,
    ].filter(Boolean).join(" "));
    const terms = Array.from(new Set(query.split(" ").filter((w) => (
      w.length >= 4 &&
      !stopWords.has(w) &&
      !/^\d+[ab]?$/.test(w)
    )))).slice(0, 90);
    let best = -1, bestScore = 0;
    sourceSegments.forEach((seg, i) => {
      const body = normalizeForMatch(`${seg.he || ""} ${seg.en || ""}`);
      if (!body) return;
      let score = 0;
      terms.forEach((term) => { if (body.includes(term)) score += term.length >= 7 ? 2 : 1; });
      if (/righteous/.test(query) && /righteous/.test(body)) score += 6;
      if (/wicked/.test(query) && /wicked/.test(body)) score += 6;
      if (/oath/.test(query) && /oath/.test(body)) score += 5;
      if (/צדיק/.test(query) && /צדיק/.test(body)) score += 6;
      if (/רשע/.test(query) && /רשע/.test(body)) score += 6;
      if (/משביע/.test(query) && /משביע/.test(body)) score += 5;
      if (score > bestScore) { bestScore = score; best = i; }
    });
    if (best < 0 || bestScore < 3) return { he: "", en: "", quoteHe: [], quoteEn: [] };
    const picked = sourceSegments[best];
    return {
      he: stripTags(picked.he),
      en: stripTags(picked.en),
      quoteHe: [stripTags(picked.he)].filter(Boolean),
      quoteEn: [stripTags(picked.en)].filter(Boolean),
    };
  }

  function anchorMatchesSegment(anchor, seg) {
    const normalized = (ref) => String(ref || "").replace(/_/g, " ").replace(/\s+/g, " ").trim();
    const anchorRef = normalized(anchor);
    const segRef = normalized(seg && (seg.ref || seg.id || seg._sectionRef || ""));
    if (!anchorRef || !segRef) return false;
    if (anchorRef === segRef || anchorRef.startsWith(segRef + ":")) return true;
    const range = anchorRef.match(/^(.*\D)(\d+)-(\d+)$/);
    const point = segRef.match(/^(.*\D)(\d+)$/);
    if (!range || !point || point[1] !== range[1]) return false;
    const value = parseInt(point[2], 10);
    return value >= parseInt(range[2], 10) && value <= parseInt(range[3], 10);
  }

  function quoteCandidatesForAnchors(anchors, segs, noteQuotes) {
    const hits = (segs || []).filter((seg) => anchors.some((anchor) => anchorMatchesSegment(anchor, seg)));
    const noteHits = (noteQuotes || []).filter((note) =>
      note.anchorRef && anchors.some((anchor) =>
        anchor === note.anchorRef ||
        note.anchorRef.startsWith(anchor + ":") ||
        anchor.startsWith(note.anchorRef + ":")
      )
    );
    return {
      // Explore's primary context is the passage in the work itself. Footnote
      // wording remains available afterward for source-text highlighting.
      quoteHe: hits.map((seg) => stripMarkers(seg.he)).concat(noteHits.flatMap((note) => note.quoteHe || [])).filter(Boolean).slice(0, 6),
      quoteEn: hits.map((seg) => stripMarkers(seg.en)).concat(noteHits.flatMap((note) => note.quoteEn || [])).filter(Boolean).slice(0, 6),
    };
  }

  function noteRefAliasMatches(ref, text) {
    const pairs = [
      [/^Proverbs\b/i, /(?:משלי|Mishlei|Proverbs)/i],
      [/^Psalms\b/i, /(?:תהלים|Tehillim|Psalms)/i],
      [/^Genesis\b/i, /(?:בראשית|Bereish|Genesis)/i],
      [/^Exodus\b/i, /(?:שמות|Shemos|Shemot|Exodus)/i],
      [/^Leviticus\b/i, /(?:ויקרא|Vayikra|Leviticus)/i],
      [/^Numbers\b/i, /(?:במדבר|Bamidbar|Numbers)/i],
      [/^Deuteronomy\b/i, /(?:דברים|Devarim|Deuteronomy)/i],
      [/^Isaiah\b/i, /(?:ישעי|Yeshaya|Isaiah)/i],
      [/^Jeremiah\b/i, /(?:ירמי|Yirmiyahu|Jeremiah)/i],
      [/^Ezekiel\b/i, /(?:יחזקאל|Yechezkel|Ezekiel)/i],
      [/^Beitzah\b/i, /(?:ביצה|Beitzah)/i],
      [/^Berakhot\b/i, /(?:ברכות|Beracho|Berakho)/i],
      [/^Shabbat\b/i, /(?:שבת|Shabbos|Shabbat)/i],
      [/^Likkutei Torah\b/i, /(?:לקו[״"']?ת|לקוטי תורה|Likkutei Torah)/i],
      [/^Torah Ohr\b/i, /(?:תו[״"']?א|תורה אור|Torah O[hr])/i],
      [/^Tanya\b/i, /(?:תניא|Tanya)/i],
      [/^Zohar\b/i, /(?:זוהר|זהר|Zohar)/i],
    ];
    const pair = pairs.find(([refPattern]) => refPattern.test(String(ref || "")));
    return !!(pair && pair[1].test(String(text || "")));
  }

  function relatedFootnoteFor(ref, group, anchors, noteQuotes, usedNs) {
    const exactCandidates = (noteQuotes || []).filter((note) => {
      if (!note || !note.n || usedNs.has(note.n)) return false;
      if (!anchors.length) return true;
      return anchors.some((anchor) => anchorMatchesSegment(anchor, { ref: note.segmentRef || note.anchorRef }));
    });
    // Some imported citation graphs only identify the section. In that case,
    // constrain candidates to notes inside that section and then require a
    // source-name or source-type match. This is precise without making a
    // section-level link appear on every paragraph.
    const sectionCandidates = exactCandidates.length ? [] : (noteQuotes || []).filter((note) => {
      if (!note || !note.n || usedNs.has(note.n)) return false;
      if (!anchors.length) return true;
      const noteRef = String(note.segmentRef || note.anchorRef || "").replace(/:\d+$/, "");
      return anchors.some((anchor) => {
        const scope = String(anchor || "").replace(/:\d+$/, "");
        return noteRef === scope || String(note.segmentRef || "").startsWith(String(anchor || "") + ":");
      });
    });
    const candidates = exactCandidates.length ? exactCandidates : sectionCandidates;
    const targetType = typeForGroup(group);
    const typeMatches = candidates.filter((note) => classify(note.text) === targetType);
    return candidates.find((note) => noteRefAliasMatches(ref, note.text)) || typeMatches[0] || null;
  }

  function cleanRelatedLink(raw) {
    if (!raw || typeof raw !== "object") return raw;
    const cleaned = Object.assign({}, raw);
    ["title", "relationship", "he", "en", "sourceHeRef", "heTitle", "anchorText", "anchorTextPlain", "anchorTextHe", "heAnchorText", "anchorTextEn", "enAnchorText", "commentator"].forEach((key) => {
      if (typeof cleaned[key] === "string") cleaned[key] = stripTags(cleaned[key]);
    });
    return cleaned;
  }

  function normalizeRelatedLinks(links, startN, segs, noteQuotes) {
    if (!Array.isArray(links) || !links.length) return [];
    const merged = new Map();
    for (const item of links) {
      const raw = cleanRelatedLink(item);
      const ref = raw && (raw.ref || raw.sourceRef);
      if (!ref) continue;
      const rawCategory = raw.displayCategory || raw.category || "Other";
      const key = `${ref}|${rawCategory}`;
      const anchors = [...new Set([
        ...(Array.isArray(raw.anchorRefs) ? raw.anchorRefs : []),
        ...(Array.isArray(raw.anchorRefExpanded) ? raw.anchorRefExpanded : []),
        ...(raw.anchorRef ? [raw.anchorRef] : []),
      ].filter((item) => typeof item === "string" && item))];
      const prior = merged.get(key);
      if (prior) {
        prior._anchors = [...new Set([...(prior._anchors || []), ...anchors])];
        prior.anchorText = [...new Set([prior.anchorText, raw.anchorText, raw.anchorTextPlain].filter(Boolean))].join(" · ");
      } else merged.set(key, Object.assign({}, raw, { ref, _anchors: anchors }));
    }
    let n = startN || 0;
    const out = [];
    const usedNoteNs = new Set();
    for (const l of merged.values()) {
      const ref = l && l.ref;
      if (!ref) continue;
      const rawCategory = l.displayCategory || l.category || "Other";
      n++;
      const isFootnotes = /,\s*Footnotes\b/i.test(`${ref} ${l.title || ""} ${l.index_title || ""}`);
      const group = isFootnotes ? "Quoted Footnotes" : displayCategory(rawCategory);
      const anchors = l._anchors || [];
      const originNote = !isFootnotes ? relatedFootnoteFor(ref, group, anchors, noteQuotes, usedNoteNs) : null;
      if (originNote) usedNoteNs.add(originNote.n);
      const quoteCandidates = quoteCandidatesForAnchors(anchors, segs, noteQuotes);
      out.push({
        n,
        id: `rel-${n}-${ref}`,
        type: typeForGroup(group),
        category: rawCategory,
        displayCategory: group,
        relationship: l.relationship || l.commentator || group,
        title: l.title || ref.replace(/\s+\d.*$/, ""),
        citation: ref,
        he: "",
        en: "",
        heCitation: l.sourceHeRef || l.heTitle || "",
        ai: "",
        srcRef: ref,
        anchorRef: originNote && originNote.segmentRef || l.anchorRef || anchors[0] || "",
        anchorRefs: anchors,
        originFootnoteN: originNote && originNote.n || null,
        anchorCount: anchors.length,
        linkType: l.linkType || "",
        compDate: Array.isArray(l.compDate) ? l.compDate : [],
        anchorVerse: l.anchorVerse,
        quoteHe: quoteCandidates.quoteHe,
        quoteEn: quoteCandidates.quoteEn,
        anchorText: l.anchorText || l.anchorTextPlain || "",
        anchorTextHe: l.anchorTextHe || l.heAnchorText || "",
        anchorTextEn: l.anchorTextEn || l.enAnchorText || "",
        commentaryNum: l.commentaryNum,
        sourceHasEn: !!l.sourceHasEn,
        related: true,
        quoted: /\b(?:quote|quoted|quotation|citation)/i.test(`${l.type || ""} ${rawCategory} ${l.linkType || ""}`),
      });
    }
    return out;
  }

  function normalizeRelatedResources(resources) {
    if (!Array.isArray(resources)) return [];
    return resources.filter((r) => r && r.kind && r.title).map((resource) => {
      const cleaned = Object.assign({}, resource);
      ["title", "heTitle", "description", "source"].forEach((key) => {
        if (typeof cleaned[key] === "string") cleaned[key] = stripTags(cleaned[key]);
      });
      return cleaned;
    });
  }

  function sourceIdentity(source) {
    const ref = String(source && (source.srcRef || source.citation) || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
    if (!ref) return "";
    const anchor = String(source.anchorRef || (Array.isArray(source.anchorRefs) ? source.anchorRefs[0] : "") || "")
      .replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
    return `${ref}|${anchor}`;
  }

  function dedupeSources(...groups) {
    const seen = new Set();
    return groups.flat().filter((source) => {
      const key = sourceIdentity(source);
      if (!key || source.footnote) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function mergeExploreSources(sources) {
    const merged = new Map();
    for (const source of sources || []) {
      if (!source || source.footnote) continue;
      const ref = String(source.srcRef || source.citation || source.id || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
      if (!ref) continue;
      const key = `${ref}|${source.displayCategory || source.category || source.type || ""}`;
      const prior = merged.get(key);
      if (!prior) {
        merged.set(key, Object.assign({}, source, {
          anchorRefs: [...new Set([...(source.anchorRefs || []), source.anchorRef].filter(Boolean))],
          quoteHe: [...new Set(source.quoteHe || [])],
          quoteEn: [...new Set(source.quoteEn || [])],
        }));
        continue;
      }
      prior.anchorRefs = [...new Set([...(prior.anchorRefs || []), ...(source.anchorRefs || []), source.anchorRef].filter(Boolean))];
      prior.anchorRef = prior.anchorRef || source.anchorRef || prior.anchorRefs[0] || "";
      prior.anchorCount = prior.anchorRefs.length;
      prior.quoteHe = [...new Set([...(prior.quoteHe || []), ...(source.quoteHe || [])].filter(Boolean))].slice(0, 8);
      prior.quoteEn = [...new Set([...(prior.quoteEn || []), ...(source.quoteEn || [])].filter(Boolean))].slice(0, 8);
      prior.contextHe = prior.contextHe || source.contextHe || "";
      prior.contextEn = prior.contextEn || source.contextEn || "";
      prior.he = prior.he || source.he || "";
      prior.en = prior.en || source.en || "";
    }
    return [...merged.values()].map((source, index) => Object.assign({}, source, { n: index + 1, id: `explore-${index + 1}-${source.id}` }));
  }

  async function loadExploreSources(refs) {
    const requested = [...new Set((refs || []).filter(Boolean))];
    const texts = (await Promise.all(requested.map((ref) => fetchSection(ref)))).filter(Boolean);
    const groups = await Promise.all(texts.map(async (t) => {
      const built = decorateSection(t, buildSection(t, 0), false);
      const citationSources = citationSourceStubs(built.sources, built.lastN, built.segs);
      const [quoteLinks, relatedLinks] = await Promise.all([
        readingUnitData(t, getQuoteLinks),
        readingUnitData(t, (ref) => getJSON(`/bff/links/${slugify(ref)}`)),
      ]);
      const quoteSources = normalizeRelatedLinks(quoteLinks, built.lastN + citationSources.length, built.segs, built.noteQuotes);
      const relatedSources = normalizeRelatedLinks(relatedLinks, built.lastN + citationSources.length + quoteSources.length, built.segs, built.noteQuotes);
      return [...citationSources, ...quoteSources, ...relatedSources];
    }));
    return mergeExploreSources(groups.flat());
  }

  const fullMapCache = new Map();
  window.loadWholeSichaSources = function loadWholeSichaSources(ref) {
    if (!fullMapCache.has(ref)) {
      const pending = (async () => {
        const text = await fetchWholeText(ref);
        if (!text) throw new Error("Could not load the complete text");
        const refs = (text.units || [text]).map(unit => unit.readerRef || unit.ref);
        const sources = await loadExploreSources(refs);
        return { refs, sources, label: text.readingUnit?.title || text.readerRef || text.ref, he: text.heTitle || "" };
      })().catch(error => { fullMapCache.delete(ref); throw error; });
      fullMapCache.set(ref, pending);
    }
    return fullMapCache.get(ref);
  };

  function refreshSourceMap() {
    const all = window.SOURCES || [];
    const merged = new Map();
    // Footnote notes remain available in the Notes rail. Only canonical,
    // openable source records count as quoted-source connections.
    all.filter((source) => source.quoted && !source.footnote && (source.srcRef || source.citation)).forEach((source) => {
      const ref = source.srcRef || source.citation || "";
      const key = ref ? `ref:${ref}` : `footnote:${source.quoteKey || source.id}`;
      const anchors = [...new Set([...(source.anchorRefs || []), ...(source.anchorRef ? [source.anchorRef] : [])].filter(Boolean))];
      const prior = merged.get(key);
      if (!prior) {
        merged.set(key, Object.assign({}, source, { anchorRefs: anchors, anchorCount: Math.max(Number(source.anchorCount || 0), anchors.length || 1) }));
        return;
      }
      prior.anchorRefs = [...new Set([...(prior.anchorRefs || []), ...anchors])];
      prior.anchorRef = prior.anchorRef || source.anchorRef || prior.anchorRefs[0] || "";
      prior.anchorCount = Math.max(Number(prior.anchorCount || 0), Number(source.anchorCount || 0), prior.anchorRefs.length || 1);
      ["quoteHe", "quoteEn"].forEach((field) => { prior[field] = [...new Set([...(prior[field] || []), ...(source[field] || [])])].slice(0, 12); });
      if ((!prior.he || /Open the source/i.test(prior.en || "")) && (source.he || source.en)) { prior.he = source.he || prior.he; prior.en = source.en || prior.en; }
    });
    window.QUOTED_SOURCES = [...merged.values()].map((source, index) => Object.assign({}, source, { n: index + 1 }));
    if (window.SICHA && window.SICHA.meta) window.SICHA.meta = Object.assign({}, window.SICHA.meta, { quotedSources: window.QUOTED_SOURCES.length });
    if (window.rebuildSourceMap) window.rebuildSourceMap();
  }

  function notifyLiveData(reason) {
    try { window.dispatchEvent(new CustomEvent("mayanos-live-data", { detail: { reason } })); } catch (e) {}
    if (window.__mayanosRerender) window.__mayanosRerender();
  }

  async function fetchSectionUncached(refOrTitle, versions) {
    const versionQuery = new URLSearchParams();
    versionQuery.set("build", "20261014-source-workflow");
    if (versions && versions.he) versionQuery.set("vhe", versions.he);
    if (versions && versions.en) versionQuery.set("ven", versions.en);
    const suffix = versionQuery.toString() ? `?${versionQuery.toString()}` : "";
    let t = await getJSON(`/bff/text/${slugify(refOrTitle)}${suffix}`);
    return (t && t.segments && t.segments.length) ? t : null;
  }
  // One request per section: the first paint and the complete discourse both
  // need this exact fetch, and a hover preheat reuses it.
  const sectionCache = new Map();
  function fetchSection(refOrTitle, versions) {
    const key = JSON.stringify([refOrTitle, (versions && versions.he) || "", (versions && versions.en) || ""]);
    if (!sectionCache.has(key)) {
      const pending = fetchSectionUncached(refOrTitle, versions);
      sectionCache.set(key, pending);
      pending.then((text) => {
        if (!text && sectionCache.get(key) === pending) sectionCache.delete(key);
      }, () => {
        if (sectionCache.get(key) === pending) sectionCache.delete(key);
      });
      if (sectionCache.size > 40) sectionCache.delete(sectionCache.keys().next().value);
    }
    return sectionCache.get(key).then((text) => (text ? { ...text } : null));
  }

  async function readingUnitData(text, fetcher) {
    const units = text.units || [text];
    const results = new Array(units.length);
    let cursor = 0;
    await Promise.all(Array.from({ length: Math.min(fetcher === getQuoteLinks ? 1 : 4, units.length) }, async () => {
      while (cursor < units.length) { const index = cursor++; results[index] = await fetcher(units[index].ref); }
    }));
    return results.flatMap((items) => Array.isArray(items) ? items : []);
  }

  const readingUnitCache = new Map();
  const firstUnitListeners = new Map();
  const fetchWholeText = (ref, versions, onFirstUnit) => {
    const key = JSON.stringify([ref, versions?.he || "", versions?.en || ""]);
    if (onFirstUnit) {
      const listeners = firstUnitListeners.get(key) || new Set();
      listeners.add(onFirstUnit);
      firstUnitListeners.set(key, listeners);
    }
    if (!readingUnitCache.has(key)) {
      const announceFirstUnit = (unit) => {
        const listeners = firstUnitListeners.get(key);
        if (!listeners) return;
        for (const listener of listeners) { try { listener(unit); } catch (e) {} }
      };
      const pending = fetchReadingUnit(ref, versions, fetchSection,
        (book) => getJSON(`/bff/toc/${slugify(book)}?sections=1`), announceFirstUnit);
      readingUnitCache.set(key, pending);
      const settle = () => { firstUnitListeners.delete(key); };
      pending.then(text => { if (!text && readingUnitCache.get(key) === pending) readingUnitCache.delete(key); settle(); }, () => {
        if (readingUnitCache.get(key) === pending) readingUnitCache.delete(key);
        settle();
      });
      if (readingUnitCache.size > 8) readingUnitCache.delete(readingUnitCache.keys().next().value);
    }
    return readingUnitCache.get(key).then(text => text ? { ...text } : null);
  };
  // Warm only the next letter's opening section: enough to paint instantly,
  // without spending the reader's bandwidth on a whole discourse up front.
  const prefetchNextText = (text) => {
    if (text?.next) void fetchSection(text.next, text.selectedVersions).catch(() => {});
  };

  // Publish a text (or, for a letter that is still loading, its opening
  // section) to the reader. The first paint and the completed letter run this
  // same path so a preview can never drift from the finished unit.
  function publishLiveText(t, options, publish) {
    const isPreview = !!(publish && publish.preview);
    if (options.focusSection) {
      t.focusRef = sectionFocus(t, options.focusSection) || t.focusRef;
    }
    window.__mayanosTOC = null;
    const built = decorateSection(t, buildSection(t, 0), false);
    const navigationRef = t.tocTargetRef || t.ref;
    const landingRef = t.focusRef || (built.segs[0] && built.segs[0].ref) || "";
    window.__fnLast = built.lastN;
    window.__loadingMore = false;
    window.__loadingPrevious = false;
    window.__loadingAdjacent = false;
    const discourseRef = String(t.readerRef || t.ref || "").replace(/,?\s*Section\s+\d+.*$/i, "");
    const catalogEntry = (window.MAYANOS_MELUKAT_BETA || []).find(item => String(item.ref || "").replace(/,?\s*Section\s+\d+.*$/i, "") === discourseRef);
    const inheritedOrigin = readerOrigin(options, historyObject(history.state));
    window.MAYANOS_RETURN_ORIGIN = inheritedOrigin;
    window.SICHA = {
      id: navigationRef,
      collection: t.collection || (t.categories || []).slice(-1)[0] || "",
      volume: "",
      parsha: /Likkutei Sichos/i.test(t.collection || "") ? discourseRef.split(",").slice(1, -1).map(part => part.trim()).join(" · ") : (t.categories || []).slice(-1)[0] || "",
      titleHe: t.farbrengen?.title || (catalogEntry ? [catalogEntry.titleHe, catalogEntry.dateHe].filter(Boolean).join(" · ") : t.heTitle || ""),
      titleEn: t.ref,
      subtitleEn: t.hasEnglish ? "" : "Hebrew source · translation coming soon",
      meta: { sources: built.sources.length, words: 0, readMins: Math.max(1, Math.round(built.segs.length * 1.3)), translated: !!t.hasEnglish },
      categories: t.categories || [],
      heCollection: t.heCollection || "",
      primaryDirection: t.primaryDirection || "",
      alternateTexts: options.alternateTexts || [],
      versionsAvailable: t.versionsAvailable || {},
      versionDetails: t.versionDetails || [],
      selectedVersions: t.selectedVersions || {},
      translationCoverage: t.translationCoverage || null,
      segmentCount: (t.segments || []).length,
      segments: built.segs,
      // A preview is a single section, so its neighbour refs point at the
      // letter's other sections, not at the next letter.
      next: isPreview ? null : (t.next || null),
      prev: isPreview ? null : (t.prev || null),
      _live: true,
      _sourceView: !!options.sourceAttemptId,
      _liveRef: t.ref,
      _liveTargetRef: navigationRef,
      _readerRef: t.readerRef || t.ref,
      _focusRef: landingRef,
      // True while only the opened section is on screen and the rest of the
      // letter is still arriving.
      _gathering: isPreview,
      _loadedSections: (t.units || [t]).map(sectionRecord),
    };
    window.SOURCES = built.sources;
    window.QUOTED_SOURCES = built.sources;
    window.RELATED_RESOURCES = [];
    window.BETA_FOOTNOTES = [];
    delete window.__mayanosPendingSegmentRef;
    if (t.focusRef && options.focus !== false) window.__mayanosPendingSegmentRef = t.focusRef;
    // Only the first paint repositions the reader; the completion pass must not
    // yank someone who already started reading back to the top.
    window.__mayanosPendingReaderStart = (publish && publish.skipStartScroll) ? false : !t.focusRef;
    if (!isPreview && options.recordActivity !== false) {
      if (window.MAYANOS_ACTIVITY) {
        window.MAYANOS_ACTIVITY.record(window.SICHA);
        window.CONTINUE = window.MAYANOS_ACTIVITY.cards();
      }
    }
    refreshSourceMap();
    if (!isPreview && options.restore) window.__mayanosPendingReaderRestore = options.restore;
    notifyLiveData("text");
    setStatus("ready", "");
    if (!isPreview) {
      prefetchNextText(window.SICHA);
      if (t.focusRef && options.focus !== false) {
        setTimeout(() => window.dispatchEvent(new CustomEvent("mayanos-scroll-segment", { detail: { ref: t.focusRef } })), 0);
      }
    }
    return { built, navigationRef, inheritedOrigin };
  }

  async function loadSicha(refOrTitle, options) {
    options = options || {};
    if (!options.sourceAttemptId) cancelSourceNavigation("another_text_selected");
    const generation = (window.__mayanosTextGeneration || 0) + 1;
    window.__mayanosTextGeneration = generation;
    const hadLiveText = !!(window.SICHA && window.SICHA._live);
    // The waiting screen must appear the moment a text is pressed, so a
    // navigation announces itself even when another text is already open.
    const showLoading = options.showLoading !== false;
    if (!hadLiveText) setStatus("loading", "Loading the library…", describeTarget(refOrTitle));
    else if (showLoading) setStatus("loading", "Gathering the sefer…", describeTarget(refOrTitle));
    // A letter is a reading unit of many sections, and sections arrive as
    // separate requests (~1-2s each, four at a time). Wait only for the one
    // section that was opened, paint it, then complete the letter underneath.
    let resolveFirstUnit = null;
    const firstUnit = new Promise((resolve) => { resolveFirstUnit = resolve; });
    const fullText = options.sourceText ? Promise.resolve(options.sourceText) : fetchWholeText(refOrTitle, options && options.versions, (unit) => resolveFirstUnit(unit || null));
    fullText.then(() => resolveFirstUnit(null), () => resolveFirstUnit(null));
    const firstSection = await firstUnit;
    let previewed = false;
    if (firstSection && firstSection.segments && firstSection.segments.length && generation === window.__mayanosTextGeneration) {
      // A preview that cannot assemble must never block the complete letter.
      try {
        publishLiveText({ ...firstSection, units: [firstSection], segments: firstSection.segments }, options, { preview: true });
        previewed = true;
      } catch (e) { previewed = false; }
    }
    // A fetch that rejects is a failed open, never an endless "connecting".
    let t = await fullText.catch(() => null);
    // A letter is only complete when every section arrived, so a transient
    // failure is retried before the reader is told the letter is incomplete.
    for (let attempt = 0; !t && attempt < 2; attempt += 1) {
      if (generation !== window.__mayanosTextGeneration) return false;
      await new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1)));
      if (generation !== window.__mayanosTextGeneration) return false;
      t = await fetchWholeText(refOrTitle, options && options.versions).catch(() => null);
    }
    // A slower request for the previous click must never replace the text
    // selected by a newer click.
    if (generation !== window.__mayanosTextGeneration) return false;
    if (!t) {
      if (options.failureMode === "preserve" && hadLiveText && !previewed) {
        setStatus("ready", "");
        return false;
      }
      const missingRef = window.__mayanosMissingRef;
      window.__mayanosMissingRef = "";
      if (missingRef && !previewed) { setStatus("not-found", NOT_FOUND_MESSAGE); return false; }
      // Never leave a half-loaded letter on screen without saying so.
      setStatus("unavailable", previewed
        ? "The rest of this sefer could not be loaded. Try again to read all of it."
        : hadLiveText
          ? `Could not open “${refOrTitle}”. Your current text is still available.`
          : "The library service is unavailable or its corpus has not been loaded.");
      return false;
    }
    let published = null;
    try { published = publishLiveText(t, options, { skipStartScroll: previewed }); } catch (e) { published = null; }
    if (!published) {
      setStatus(hadLiveText || previewed ? "ready" : "unavailable", hadLiveText || previewed
        ? `Could not open “${refOrTitle}”. Your current text is still available.`
        : "The library service is unavailable or its corpus has not been loaded.");
      return false;
    }
    const { built, navigationRef, inheritedOrigin } = published;
    const tocPromise = loadTOC(window.SICHA.collection || t.ref);
    // Opening a chelek from the library asks for the letter chooser rather
    // than silently landing on the first maamar, so open it once the TOC is in.
    if (options.openContents) {
      const openContents = () => { try { window.dispatchEvent(new CustomEvent("mayanos-open-contents")); } catch (e) {} };
      tocPromise.then(openContents, openContents);
    }
    // Keep the canonical, shareable reader URL in sync with the loaded text.
    if (options.syncUrl !== false) {
      try {
        const internalRef = t.readerRef || t.ref;
        const nextUrl = canonicalReaderPathFor(t.collection || window.SICHA.collection, internalRef, t.focusRef, window.__mayanosTOC)
          || "/reader/" + slugify(t.focusRef || internalRef);
        if (location.pathname + location.search !== nextUrl) {
          const nextState = inheritedOrigin ? { mayanosOrigin: inheritedOrigin } : {};
          const openingLegacyLink = /^\/reader\//.test(location.pathname);
          const historyMode = options.historyMode || (openingLegacyLink && !options.origin ? "replace" : "push");
          history[historyMode === "replace" ? "replaceState" : "pushState"](nextState, "", nextUrl);
        } else if (Object.prototype.hasOwnProperty.call(options, "origin")) {
          history.replaceState(Object.assign({}, historyObject(history.state), { mayanosOrigin: inheritedOrigin }), "", nextUrl);
        }
      } catch (e) {}
    }
    if (options.recordActivity !== false && window.MAYANOS_ANALYTICS) {
        window.MAYANOS_ANALYTICS.track("text_opened", {
          bookRef: t.ref,
          collection: window.SICHA.collection || "",
          title: t.heTitle || t.ref,
          hasEnglish: !!t.hasEnglish,
          categories: t.categories || [],
        });
      }
    // Some books do not encode maamar/seif in the internal ref. Once their
    // TOC arrives, normalize the same history entry to the clean route too.
    tocPromise.then((loadedToc) => {
      if (options.syncUrl === false || !window.SICHA || window.SICHA._liveTargetRef !== navigationRef) return;
      const resolvedPath = canonicalReaderPathFor(t.collection || window.SICHA.collection, t.readerRef || t.ref, t.focusRef, loadedToc);
      if (resolvedPath) history.replaceState(historyObject(history.state), "", resolvedPath);
    }).catch(() => {});
    let progressiveCitationSources = [];
    let progressiveQuoteSources = [];
    // Reserve the citation number range synchronously so quote discovery can
    // render immediately without colliding with citations that are still
    // fetching their full previews.
    const citationSlotCount = citationSourceStubs(built.sources, built.lastN, built.segs).length;
    window.__fnLast = Math.max(window.__fnLast || 0, built.lastN + citationSlotCount);
    const isCurrentText = () => generation === window.__mayanosTextGeneration
      && window.SICHA && window.SICHA._liveTargetRef === navigationRef;
    const applyProgressiveSources = (citationSources) => {
      if (!isCurrentText()) return;
      progressiveCitationSources = citationSources || [];
      window.SOURCES = dedupeSources(window.SOURCES || built.sources, progressiveCitationSources, progressiveQuoteSources);
      window.SICHA.meta = Object.assign({}, window.SICHA.meta, { sources: window.SOURCES.length });
      refreshSourceMap();
      notifyLiveData("citation-progress");
    };
    const quoteLinksPromise = readingUnitData(t, getQuoteLinks).then((quoteLinks) => {
      if (!isCurrentText()) return quoteLinks;
      progressiveQuoteSources = normalizeRelatedLinks(quoteLinks, window.__fnLast || 0, built.segs, built.noteQuotes);
      window.__fnLast = (window.__fnLast || 0) + progressiveQuoteSources.length;
      window.SOURCES = dedupeSources(window.SOURCES || built.sources, progressiveCitationSources, progressiveQuoteSources);
      window.SICHA.meta = Object.assign({}, window.SICHA.meta, { sources: window.SOURCES.length });
      refreshSourceMap();
      notifyLiveData("quote-progress");
      return quoteLinks;
    });
    window.__mayanosSourceHydration = Promise.all([
      readingUnitData(t, (ref) => getJSON(`/bff/links/${slugify(ref)}`)),
      readingUnitData(t, (ref) => getJSON(`/bff/resources/${slugify(ref)}`)),
      quoteLinksPromise,
      hydrateCitationSources(built.sources, built.lastN, built.segs, applyProgressiveSources),
    ]).then(([relatedLinks, relatedResources, quoteLinks, citationSources]) => {
      if (!isCurrentText()) return;
      const quoteSources = progressiveQuoteSources;
      const relatedSources = normalizeRelatedLinks(relatedLinks, window.__fnLast || 0, built.segs, built.noteQuotes);
      window.__fnLast = (window.__fnLast || 0) + relatedSources.length;
      window.SOURCES = dedupeSources(window.SOURCES || built.sources, citationSources, quoteSources, relatedSources);
      const existingResources = window.RELATED_RESOURCES || [];
      const resourceMap = new Map(existingResources.concat(normalizeRelatedResources(relatedResources)).map((resource) => [`${resource.kind}|${resource.id || resource.url || resource.title}`, resource]));
      window.RELATED_RESOURCES = [...resourceMap.values()];
      const liveTopics = window.RELATED_RESOURCES.filter((resource) => resource.kind === "topic").map((resource) => ({
        id: resource.id, slug: resource.id, en: resource.title, he: resource.heTitle || "", count: resource.count,
      }));
      if (liveTopics.length) window.TOPICS = liveTopics.slice(0, 18);
      window.SICHA.meta = Object.assign({}, window.SICHA.meta, { sources: window.SOURCES.length });
      refreshSourceMap();
      notifyLiveData("sources");
    }).catch(() => {});
    return true;
  }

  async function loadSichaVersion(language, title) {
    const current = window.SICHA;
    if (!current || !current._liveRef || !title) return false;
    const versions = Object.assign({}, current.selectedVersions || {}, { [language]: title });
    // Switching edition re-renders the text that is already on screen, so it
    // must never trade the reader for the waiting screen.
    return loadSicha(current._liveTargetRef || current._liveRef, { versions, recordActivity: false, syncUrl: false, showLoading: false });
  }

  // Continuous reader: load either neighboring section while keeping a single
  // source-number namespace for everything currently mounted in the reader.
  async function loadAdjacent(direction) {
    if (window.__loadingAdjacent) return false;
    const request = {};
    window.__loadingAdjacent = request;
    const cur = window.SICHA;
    const generation = window.__mayanosTextGeneration || 0;
    const isCurrentText = () => generation === window.__mayanosTextGeneration && window.SICHA === cur;
    try {
      const boundaryRef = direction === "previous" ? cur?.prev : cur?.next;
      if (!cur?._live || !boundaryRef) return false;
      window[direction === "previous" ? "__loadingPrevious" : "__loadingMore"] = true;
      const t = await fetchWholeText(boundaryRef, cur.selectedVersions);
      if (!t || !isCurrentText()) return false;
      const navigationRef = t.tocTargetRef || t.ref;
      const loadedRefs = new Set((cur._loadedSections || []).map(section => section.targetRef || section.ref));
      if (loadedRefs.has(navigationRef)) {
        if (direction === "previous") cur.prev = t.prev || null;
        else cur.next = t.next || null;
        return false;
      }
      const built = decorateSection(t, buildSection(t, window.__fnLast || 0), true);
      // Reserve footnotes and citation slots before any asynchronous source work.
      // Other texts may mount while these previews are still arriving.
      const citationSlots = citationSourceStubs(built.sources, built.lastN, built.segs).length;
      window.__fnLast = built.lastN + citationSlots;
      if (direction === "previous") {
        cur.segments = built.segs.concat(cur.segments);
        cur.prev = t.prev || null;
        cur._loadedSections = (t.units || [t]).map(sectionRecord).concat(cur._loadedSections || []);
      } else {
        cur.segments = cur.segments.concat(built.segs);
        cur.next = t.next || null;
        cur._loadedSections = (cur._loadedSections || []).concat((t.units || [t]).map(sectionRecord));
      }
      window.SOURCES = dedupeSources(window.SOURCES || [], built.sources);
      const translatedSegments = cur.segments.filter(segment => String(segment.en || "").replace(/<[^>]*>/g, "").trim()).length;
      cur.segmentCount = cur.segments.length;
      cur.translationCoverage = { translatedSegments, totalSegments: cur.segmentCount,
        percent: Math.round(translatedSegments / cur.segmentCount * 100) };
      cur.meta = Object.assign({}, cur.meta, { sources: window.SOURCES.length, translated: translatedSegments > 0 });
      refreshSourceMap();
      notifyLiveData(direction === "previous" ? "previous" : "more");
      prefetchNextText(cur);
      // Reading never waits for citation previews, discovery, or related resources.
      void Promise.all([
        readingUnitData(t, ref => getJSON(`/bff/links/${slugify(ref)}`)),
        readingUnitData(t, ref => getJSON(`/bff/resources/${slugify(ref)}`)),
        readingUnitData(t, getQuoteLinks),
        hydrateCitationSources(built.sources, built.lastN, built.segs),
      ]).then(([relatedLinks, relatedResources, quoteLinks, citationSources]) => {
        if (!isCurrentText()) return;
        const quoteSources = normalizeRelatedLinks(quoteLinks, window.__fnLast || 0, built.segs, built.noteQuotes);
        window.__fnLast = (window.__fnLast || 0) + quoteSources.length;
        const relatedSources = normalizeRelatedLinks(relatedLinks, window.__fnLast || 0, built.segs, built.noteQuotes);
        window.__fnLast += relatedSources.length;
        window.SOURCES = dedupeSources(window.SOURCES || [], citationSources, quoteSources, relatedSources);
        window.RELATED_RESOURCES = (window.RELATED_RESOURCES || []).concat(normalizeRelatedResources(relatedResources));
        cur.meta = Object.assign({}, cur.meta, { sources: window.SOURCES.length });
        refreshSourceMap();
        notifyLiveData("sources");
      }).catch(() => {});
      return { ref: t.ref, direction, firstSegmentRef: built.segs[0]?.ref };
    } finally {
      if (window.__loadingAdjacent === request) {
        window.__loadingAdjacent = false;
        window.__loadingPrevious = false;
        window.__loadingMore = false;
      }
    }
  }

  async function loadMore() {
    return loadAdjacent("next");
  }

  async function loadPrevious() {
    return loadAdjacent("previous");
  }

  // "Open source" on a footnote card → navigate to the cited mareh makom.
  // Footnotes vary: some carry the ref as a bold title ("Berachos 58b:"), others
  // as the whole body ("Proverbs 20:16."). Try every plausible citation.
  let sourceOpenGeneration = 0;
  let pendingSource = null;
  const SOURCE_ATTEMPT_KEY = "mayanos-pending-source-v3";
  const announceSource = message => window.dispatchEvent(new CustomEvent("mayanos-source-notice", { detail: { message } }));
  function cancelSourceNavigation(reason = "navigated_away") {
    if (!pendingSource) return;
    const attempt = pendingSource;
    if (attempt.loading) window.__mayanosTextGeneration = (window.__mayanosTextGeneration || 0) + 1;
    attempt.finish("source_open_cancelled", { reason });
  }
  window.cancelSourceNavigation = cancelSourceNavigation;
  // Recover an interrupted attempt after page navigation/reload. IDs let the
  // dashboard correlate each attempt with its terminal outcome.
  try {
    const interrupted = JSON.parse(sessionStorage.getItem(SOURCE_ATTEMPT_KEY) || "null");
    if (interrupted && window.MAYANOS_ANALYTICS) window.MAYANOS_ANALYTICS.track("source_open_cancelled",
      { ...interrupted.properties, reason: "page_left" }, { context: interrupted.context });
    sessionStorage.removeItem(SOURCE_ATTEMPT_KEY);
  } catch {}
  window.addEventListener("pagehide", () => cancelSourceNavigation("page_left"));
  window.addEventListener("popstate", () => cancelSourceNavigation("history_navigation"));

  async function openSource(src) {
    if (!src) { if (window.openPalette) window.openPalette(); return false; }
    cancelSourceNavigation("another_source_selected");
    const analytics = window.MAYANOS_ANALYTICS;
    const sourceContext = analytics && analytics.context();
    const properties = {
      attemptId: crypto.randomUUID(), trackingVersion: 3,
      sourceRef: String(src.srcRef || src.citation || src.he || src.title || "").slice(0, 500),
      sourceType: src.type || src.category || "", relationship: src.relationship || "",
      footnoteNumber: src.n || null, anchorRef: src.anchorRef || "",
    };
    const record = (name, extra = {}) => analytics && analytics.track(name, { ...properties, ...extra }, { context: sourceContext });
    const controller = new AbortController();
    let finished = false;
    let reserved = false;
    let timer;
    const attempt = { controller, id: properties.attemptId, loading: false, finish(name, extra) {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      record(name, extra);
      if (pendingSource === attempt) pendingSource = null;
      try { sessionStorage.removeItem(SOURCE_ATTEMPT_KEY); } catch {}
      if (name !== "source_opened" && name !== "source_external_opened" && name !== "source_sheet_opened") controller.abort();
    } };
    pendingSource = attempt;
    record("source_open_attempted");
    try { sessionStorage.setItem(SOURCE_ATTEMPT_KEY, JSON.stringify({ properties, context: sourceContext })); } catch {}
    timer = setTimeout(() => {
      if (attempt.loading) {
        window.__mayanosTextGeneration = (window.__mayanosTextGeneration || 0) + 1;
        if (reserved) history.back();
      }
      attempt.finish("source_open_failed", { reason: "timeout" });
      announceSource("This source took too long to load. Your original text and place are still available.");
    }, 16_000);
    const external = verifiedExternalSource(src);
    const sheetUrl = sourceSheetUrl(src);
    if (external || sheetUrl) {
      const url = external?.url || sheetUrl;
      const opened = window.open(url, "_blank");
      if (opened) opened.opener = null;
      attempt.finish(opened ? external ? "source_external_opened" : "source_sheet_opened" : "source_open_failed",
        { url, targetRef: external?.title || properties.sourceRef, reason: opened ? "external_source" : "popup_blocked" });
      announceSource(opened ? `This source opened on ${external?.publisher || "Sefaria"}. Your maamer stays open here.` : "Allow this source to open in a new tab, then try again.");
      return !!opened;
    }
    const generation = ++sourceOpenGeneration;
    const origin = currentReaderOrigin(src);
    const currentText = window.SICHA;
    announceSource("Locating the cited passage…");
    let target = "";
    let locatedText = null;
    try {
      const candidates = sourceNavigationCandidates(src, window.SOURCES || [], currentText);
      for (const candidate of candidates) {
        if (finished) return false;
        const text = await getJSON(`/bff/text/${slugify(candidate)}?build=20261014-source-workflow-163`,
          { signal: controller.signal, mayanosRequestPurpose: "source_resolution", mayanosSourceAttempt: properties.attemptId });
        if (finished) return false;
        if (generation !== sourceOpenGeneration || window.SICHA !== currentText) {
          attempt.finish("source_open_cancelled", { reason: "navigated_away" }); return false;
        }
        if (isLocatedSource(text)) {
          target = sourceTargetRef(text, src, currentText, candidate);
          const targetIndex = text.segments.findIndex(segment => segment.ref === target);
          locatedText = { ...text, focusRef: target, segments: targetIndex >= 0 ? text.segments.slice(targetIndex) : text.segments };
          break;
        }
      }
      if (!target) {
        attempt.finish("source_open_failed", { reason: "passage_not_found" });
        announceSource("We couldn’t locate the exact passage for this citation. The footnote is still available here.");
        return false;
      }
      try {
        history.replaceState(Object.assign({}, historyObject(history.state), { mayanosRestore: origin }), "", origin.url);
        sessionStorage.setItem("mayanos-return-origin", JSON.stringify(origin));
        history.pushState({ mayanosOrigin: origin }, "", "/reader/" + slugify(target));
        reserved = true;
      } catch {}
      announceSource("");
      attempt.loading = true;
      const loaded = await loadSicha(target, { origin, historyMode: "replace", failureMode: "preserve", sourceAttemptId: properties.attemptId, sourceText: locatedText });
      if (finished) return false;
      if (!loaded) {
        attempt.finish("source_open_failed", { reason: "load_failed", targetRef: target });
        if (reserved) history.back();
        announceSource("This source couldn’t be loaded. Your original text and place are still available.");
      } else attempt.finish("source_opened", { targetRef: target });
      return !!loaded;
    } catch (error) {
      if (!finished) {
        attempt.finish("source_open_failed", { reason: "unexpected_error", targetRef: target, message: error?.message || "Source unavailable" });
        if (reserved) history.back();
        announceSource("This source couldn’t be loaded. Your original text and place are still available.");
      }
      return false;
    }
  }

  // Real table of contents: the book's sections (letters/chapters).
  let tocGeneration = 0;
  const tocCacheKey = (book) => `mayanos-toc:v1:${book}`;
  const readCachedToc = (book) => {
    try {
      const raw = sessionStorage.getItem(tocCacheKey(book));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt || Date.now() - parsed.savedAt > 2 * 60 * 60 * 1000) return null;
      return parsed.toc && parsed.toc.sections ? parsed.toc : null;
    } catch (e) { return null; }
  };
  const writeCachedToc = (book, toc) => {
    try { sessionStorage.setItem(tocCacheKey(book), JSON.stringify({ savedAt: Date.now(), toc })); } catch (e) {}
  };
  async function loadTOC(book) {
    const generation = ++tocGeneration;
    if (!book) { window.__mayanosTOC = null; return null; }
    // A book's contents run 95-204 KB and the picker needs them anyway, so a
    // session copy saves that round trip for every following letter.
    const cached = readCachedToc(book);
    const toc = cached || await getJSON(`/bff/toc/${slugify(book)}?sections=1`);
    if (generation !== tocGeneration) return null;
    if (!cached && toc) writeCachedToc(book, toc);
    window.__mayanosTOC = toc && toc.sections && toc.sections.length ? toc : null;
    notifyLiveData("toc");
    return window.__mayanosTOC;
  }

  // Replace the home/library/topics list data with real library structure.
  async function loadLibrary() {
    window.LIBRARY_STATUS = "loading";
    notifyLiveData("library");
    const tree = await getJSON(`/bff/library?featured=1`);
    if (tree && Array.isArray(tree.collections) && tree.collections.length) {
      window.CORPUS_STATS = tree.customStats;
      window.LIBRARY_COUNT = Number(tree.count || tree.collections.length);
      const preferred = [
        /^Tanya$/i, /^Torah Ohr$/i, /^Likkutei Torah$/i,
        /^Hayom Yom$/i, /^Likkutei Sich/i, /^Igros Kodesh/i,
      ];
      const featured = [];
      for (const pattern of preferred) {
        const match = tree.collections.find((item) => pattern.test(item.en || item.id || ""));
        if (match && !featured.some((item) => item.id === match.id)) featured.push(match);
      }
      window.COLLECTIONS = featured.length >= 4 ? featured.slice(0, 6) : tree.collections.slice(0, 6);
    }
    const byRebbe = location.pathname === "/library" ? await getJSON(`/bff/rebbeim?build=20260923-library-cleanup`) : null;
    if (byRebbe) {
      window.CHASSIDIM_SHELVES = { otzar: byRebbe.otzar || [], around: byRebbe.around || [] };
      const merge = (arr) => (arr || []).map((r) => byRebbe[r.id] && byRebbe[r.id].length ? Object.assign({}, r, { works: byRebbe[r.id] }) : r);
      if (window.REBBEIM) window.REBBEIM = merge(window.REBBEIM);
      if (window.EARLY_CHASSIDUS) window.EARLY_CHASSIDUS = merge(window.EARLY_CHASSIDUS);
    }
    if (!window.TOPICS || !window.TOPICS.length) {
      await window.siteSettingsReady;
      const topicData = window.topicsEnabled() ? await getJSON(`/bff/topics?limit=12`) : null;
      if (topicData && Array.isArray(topicData.topics)) window.TOPICS = topicData.topics.map((topic) => ({
        id: topic.slug, slug: topic.slug, en: topic.title, he: topic.heTitle || "", count: topic.count,
      }));
    }
    window.LIBRARY_STATUS = tree && (location.pathname !== "/library" || byRebbe && Object.keys(byRebbe).length) ? "ready" : "error";
    notifyLiveData("library");
  }

  async function loadToday() {
    const localDate = new Date();
    const iso = [localDate.getFullYear(), String(localDate.getMonth() + 1).padStart(2, "0"), String(localDate.getDate()).padStart(2, "0")].join("-");
    // The day's reading is cheap to compute but must not be served from a stale
    // HTTP cache after a deploy, so revalidate it and key it on this build.
    const today = await getJSON(`/bff/today?date=${iso}&build=20260930-daily`, { cache: "no-cache" });
    if (!today || today.unavailable) return false;
    const daily = today.hayomYom || {};
    window.TODAY = {
      parsha: today.parsha || "",
      hayomyom: {
        date: today.hebrewDate || "",
        he: daily.he || "",
        en: daily.en || "",
        ref: daily.ref || "",
      },
      attribution: today.attribution || null,
    };
    window.WEEKLY_FEATURE = today.weeklyFeature ? Object.assign({}, today.weeklyFeature, { sources: [] }) : null;
    notifyLiveData("today");
    if (window.WEEKLY_FEATURE && window.WEEKLY_FEATURE.ref) {
      loadExploreSources([window.WEEKLY_FEATURE.ref]).then((sources) => {
        if (!window.WEEKLY_FEATURE || window.WEEKLY_FEATURE.ref !== today.weeklyFeature.ref) return;
        window.WEEKLY_FEATURE = Object.assign({}, window.WEEKLY_FEATURE, { sources });
        notifyLiveData("weekly-feature");
      }).catch(() => {});
    }
    return true;
  }

  function toType(cat) { return classify(cat); }

  window.MAYANOS_LIVE = true;
  window.loadSicha = loadSicha;
  // Hovering a library card warms the one request the first paint needs.
  window.__mayanosPrefetchSection = (ref, versions) => { try { void fetchSection(ref, versions); } catch (e) {} };
  window.loadSichaVersion = loadSichaVersion;
  window.loadMore = loadMore;
  window.loadPrevious = loadPrevious;
  window.loadExploreSources = loadExploreSources;
  window.loadBetaFootnotes = loadBetaFootnotes;
  window.openSource = openSource;
  window.loadLibrary = loadLibrary;
  window.loadToday = loadToday;
  window.__mayanosCanonicalReaderPathFor = canonicalReaderPathFor;
  window.__mayanosResolveCanonicalReaderRoute = resolveCanonicalReaderRoute;
  window.retryMayanos = () => {
    setStatus("loading", "Reconnecting to the Mayanos library…");
    const isReaderLink = /^\/reader\//.test(location.pathname) || /^\/r\//.test(location.pathname) || /^#reader\//.test(location.hash || "");
    return Promise.all([initialReaderRef().then((ref) => loadSicha(ref, { syncUrl: isReaderLink, recordActivity: isReaderLink })), loadLibrary(), loadToday()]);
  };
  window.dispatchEvent(new Event("mayanos-live-ready"));
  if (window.__mayanosLoadHashReader) window.__mayanosLoadHashReader();

  // Open whatever the URL hash points at (deep-linkable), else a default text.
  window.__mayanosDefaultRef = "Likkutei Sichos Vol 38";
  function initialRef() {
    const m = location.pathname.match(/^\/reader\/(.+)$/) || (location.hash || "").match(/#reader\/(.+)$/);
    if (m) {
      let ref = m[1];
      const q = ref.indexOf("?");
      if (q >= 0) ref = ref.slice(0, q);
      try { return decodeURIComponent(ref).replace(/_/g, " "); } catch (e) {}
    }
    return window.__mayanosDefaultRef;
  }
  async function initialReaderRef() {
    const match = location.pathname.match(/^\/r\/([^/]+)\/(\d+)\/(\d+)\/?$/);
    if (match) {
      const route = await resolveCanonicalReaderRoute(match[1], match[2], match[3]);
      if (route && route.ref) return route.ref;
    }
    return initialRef();
  }
  let canonicalRouteInFlight = false;
  async function openCanonicalReaderRoute() {
    if (window.__mayanosCanonicalReaderRoutes) return false;
    const match = location.pathname.match(/^\/r\/([^/]+)\/(\d+)\/(\d+)\/?$/);
    if (!match || canonicalRouteInFlight) return false;
    canonicalRouteInFlight = true;
    try {
      const route = await resolveCanonicalReaderRoute(match[1], match[2], match[3]);
      if (!route || !route.ref) { setStatus("not-found", NOT_FOUND_MESSAGE); return false; }
      const legacyPath = `/reader/${slugify(route.ref)}`;
      const state = historyObject(history.state);
      history.replaceState(Object.assign({}, state, { mayanosCanonicalInternal: true }), "", legacyPath);
      // The app router uses /reader to select the reader surface. This
      // internal transition never creates a visible history entry.
      try { window.dispatchEvent(new PopStateEvent("popstate", { state: history.state })); } catch (e) {}
      const loaded = await loadSicha(route.ref, { syncUrl: false, recordActivity: true });
      if (loaded) history.replaceState(Object.assign({}, state, { mayanosCanonicalReader: true }), "", route.path);
      return !!loaded;
    } catch (e) {
      setStatus("not-found", NOT_FOUND_MESSAGE);
      return false;
    } finally {
      canonicalRouteInFlight = false;
    }
  }
  window.addEventListener("popstate", () => {
    if (canonicalRouteInFlight || window.__mayanosCanonicalReaderRoutes) return;
    if (location.pathname.match(/^\/r\/([^/]+)\/(\d+)\/(\d+)\/?$/)) openCanonicalReaderRoute();
  });
  setTimeout(function () {
    const isReaderLink = /^\/reader\//.test(location.pathname) || /^\/r\//.test(location.pathname) || /^#reader\//.test(location.hash || "");
    if (location.pathname.match(/^\/r\/([^/]+)\/(\d+)\/(\d+)\/?$/)) {
      // The appended URL adapter owns /r/ routes once it is installed; it
      // reports its own terminal state, so this fallback must not race it.
      const adapterOwnsRoutes = !!window.__mayanosCanonicalReaderRoutes;
      openCanonicalReaderRoute().then((opened) => {
        if (opened || adapterOwnsRoutes) return;
        if (!window.MAYANOS_STATUS || window.MAYANOS_STATUS.state === "loading") setStatus("not-found", NOT_FOUND_MESSAGE);
      });
    }
    else if (isReaderLink || ["/reader", "/explore", "/topics"].includes(location.pathname)) initialReaderRef().then((ref) => loadSicha(ref, { syncUrl: isReaderLink, recordActivity: isReaderLink }));
    else setStatus("ready", "");
    if (["/", "/library"].includes(location.pathname)) loadLibrary();
    if (location.pathname === "/") {
      if (window.requestIdleCallback) window.requestIdleCallback(() => loadToday(), { timeout: 1500 });
      else setTimeout(loadToday, 400);
    }
  }, 300);
})();
